import { useState, useTransition } from "react";
import { Link, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { ar as arLocale } from "date-fns/locale";
import { Pencil, Save, Trash2, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input, Textarea, Label, Select } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Markdown } from "@/components/ui/markdown";
import type { Job, JobVisibility } from "@/lib/types";
import { useDict, useLocale, fill } from "@/lib/i18n/context";

function toDateInput(iso: string | null): string {
  if (!iso) return "";
  return iso.slice(0, 10);
}

function todayDateInput(): string {
  return new Date().toISOString().slice(0, 10);
}

function isExpired(expiresAt: string | null): boolean {
  if (!expiresAt) return false;
  return new Date(expiresAt).getTime() < Date.now();
}

export function JobEditor({
  job,
  initialEditing = false,
}: {
  job: Job;
  initialEditing?: boolean;
}) {
  const t = useDict();
  const { locale } = useLocale();
  const dateLocale = locale === "ar" ? arLocale : undefined;
  const navigate = useNavigate();
  const [editing, setEditing] = useState(initialEditing);
  const [title, setTitle] = useState(job.title);
  const [jd, setJd] = useState(job.generated_jd ?? "");
  const [visibility, setVisibility] = useState<JobVisibility>(
    job.visibility === "public" ? "public" : "private",
  );
  const [expiresAt, setExpiresAt] = useState(toDateInput(job.expires_at));
  const [pending, startTransition] = useTransition();
  const expired = isExpired(job.expires_at);

  function save() {
    startTransition(async () => {
      await fetch(`/api/jobs/${job.id}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title,
          generated_jd: jd,
          visibility,
          expires_at: expiresAt || null,
        }),
      });
      setEditing(false);
      window.location.reload();
    });
  }

  function remove() {
    if (!confirm(t.jobs.deleteConfirm)) return;
    startTransition(async () => {
      await fetch(`/api/jobs/${job.id}`, {
        method: "DELETE",
        credentials: "include",
      });
      navigate("/jobs");
    });
  }

  return (
    <Card>
      <CardContent className="space-y-4 pt-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          {editing ? (
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="max-w-md text-lg font-semibold"
            />
          ) : (
            <div>
              <h1 className="text-2xl font-bold">{job.title}</h1>
              <div className="mt-2 flex flex-wrap gap-2">
                <Badge tone="green">{t.jobs.active}</Badge>
                <Badge tone={job.visibility === "public" ? "blue" : "neutral"}>
                  {job.visibility === "public"
                    ? t.jobs.badgePublic
                    : t.jobs.badgePrivate}
                </Badge>
                {job.expires_at && (
                  <Badge tone={expired ? "red" : "amber"}>
                    {expired
                      ? t.jobs.listingExpired
                      : fill(t.jobs.listingExpires, {
                          date: format(new Date(job.expires_at), "PP", {
                            locale: dateLocale,
                          }),
                        })}
                  </Badge>
                )}
              </div>
            </div>
          )}
          <div className="flex gap-2">
            {editing ? (
              <Button onClick={save} loading={pending} size="sm">
                <Save className="h-4 w-4" /> {t.common.save}
              </Button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setEditing(true)}
              >
                <Pencil className="h-4 w-4" /> {t.common.edit}
              </Button>
            )}
            <Link to={`/candidates/new?job=${job.id}`}>
              <Button size="sm">
                <UserPlus className="h-4 w-4" /> {t.jobs.inviteCandidate}
              </Button>
            </Link>
            <Button variant="danger" size="sm" onClick={remove} disabled={pending}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {editing && (
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label>{t.jobs.listingEndDate}</Label>
              <Input
                type="date"
                value={expiresAt}
                min={todayDateInput()}
                onChange={(e) => setExpiresAt(e.target.value)}
              />
            </div>
            <div>
              <Label>{t.jobs.listingVisibility}</Label>
              <Select
                value={visibility}
                onChange={(e) =>
                  setVisibility(
                    e.target.value === "public" ? "public" : "private",
                  )
                }
              >
                <option value="private">{t.jobs.visibilityPrivate}</option>
                <option value="public">{t.jobs.visibilityPublic}</option>
              </Select>
            </div>
          </div>
        )}

        {editing ? (
          <Textarea
            value={jd}
            onChange={(e) => setJd(e.target.value)}
            className="min-h-96 font-mono text-xs"
          />
        ) : (
          <div className="rounded-xl border border-border bg-surface/50 p-5">
            <Markdown>{job.generated_jd ?? `_${t.jobs.noDescription}_`}</Markdown>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
